import useSessionStore from "@/stores/session-store";
import axios, {
  AxiosInstance,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from "axios";
import axiosRetry from "axios-retry";
import { getSession } from "next-auth/react";
import { localLogout } from "./auth-client";

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

class Http {
  instance: AxiosInstance;
  constructor() {
    const baseURL = process.env.NEXT_PUBLIC_API_ENDPOINT as string;
    this.instance = axios.create({
      baseURL: baseURL,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    });
    axiosRetry(this.instance, {
      retries: 5,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: (error) =>
        axiosRetry.isNetworkOrIdempotentRequestError(error),
    });
    this.instance.interceptors.request.use(
      async (config) => {
        if (typeof window !== "undefined") {
          const { accessToken } = useSessionStore.getState();
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as
          | RetryableRequestConfig
          | undefined;

        if (
          error.response &&
          error.response.status === HttpStatusCode.Unauthorized &&
          typeof window !== "undefined"
        ) {
          // First 401 on a request: the cached access token may simply be
          // stale (e.g. user idle past its lifetime). Force NextAuth to
          // re-evaluate/refresh the session, then retry once with the
          // refreshed token before giving up and logging out.
          if (originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;

            const session = await getSession();
            const refreshedError = (session as { error?: string } | null)
              ?.error;
            const refreshedAccessToken = (
              session as { accessToken?: string } | null
            )?.accessToken;

            if (
              refreshedError === "RefreshAccessTokenError" ||
              !refreshedAccessToken
            ) {
              await localLogout();
              return Promise.reject(error);
            }

            if (refreshedError === "RefreshAccessTokenTransientError") {
              // Refresh failed temporarily (network blip, auth API hiccup).
              // The cached access token is still the expired one, so retrying
              // with it would just 401 again — surface the original error
              // instead of masking a real failure behind a second identical 401.
              return Promise.reject(error);
            }

            // Sync the store immediately — the request interceptor reads
            // from it on every call (including this retry), so leaving the
            // stale token there would clobber the header set below and make
            // the retry 401 again even though the refresh actually worked.
            useSessionStore.getState().setAccessToken(refreshedAccessToken);
            originalRequest.headers.Authorization = `Bearer ${refreshedAccessToken}`;
            return this.instance(originalRequest);
          }

          // Second 401 for this request: we already fetched the session and
          // retried once with whatever access token it reported (refreshed
          // or otherwise "valid"), and the API rejected it again. Whatever
          // NextAuth's client-side expiry check thinks, the server has
          // conclusively invalidated this token (e.g. revoked, superseded by
          // another login) — there is nothing left to retry with, so log out.
          await localLogout();
        }
        return Promise.reject(error);
      }
    );
  }
}

const http = new Http().instance;

const publicHttp: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_ENDPOINT as string,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});
axiosRetry(publicHttp, {
  retries: 5,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) =>
    axiosRetry.isNetworkOrIdempotentRequestError(error),
});

export { http, publicHttp };
